import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  Button,
  Price,
  PrimaryAction,
  useSubmitButton,
  LinkButton,
} from "../Atoms";
import { CartDetailsForm, PickUpOptions } from "../Cart/CartDetailsForm";
import { CartItems } from "../Cart/CartItems";
import { OrderedOrderItems } from "../Cart/OrderedOrderItem";
import { createCheckoutSession, updateCart } from "../Cart/requests";
import { getCartTotal } from "../Cart/selectors";
import OnlineOrderingDisabled, {
  useOnlineOrderingAvailableNow,
} from "../OnlineOrderingSchedule/OnlineOrderingDisabled";
import { useSession, useSessionDispatch } from "../Session/SessionContext";
import { useVenue } from "../VenueContext";
import {
  getVenueHasSimpleOrdering,
  getVenueHasTableService,
} from "../venueSelectors";

const FormDataSchema = z.object({
  name: z.string().min(4),
  floorTableId: z
    .string()
    .nullish()
    .transform((v) => (!v ? null : v)),
  pickUpInMinutes: z
    .union([z.string(), z.number(), z.null()])
    .optional()
    .transform((v) => (!v ? null : Number(v)))
    .nullish(),
});

export type SimpleOrderFormData = z.infer<typeof FormDataSchema>;
export const SimpleOrderCart = ({}) => {
  const { t } = useTranslation();
  const venue = useVenue();
  const tableServiceEnabled = getVenueHasTableService(venue);
  const orderingEnabled = getVenueHasSimpleOrdering(venue);
  const [submissionState, trackSubmit] = useSubmitButton();
  const session = useSession();
  const dispatch = useSessionDispatch();
  const cart = session.cart;
  const cartTotal = getCartTotal(session.cart);

  const { register, handleSubmit, getValues, ...formProps } =
    useForm<SimpleOrderFormData>({
      resolver: zodResolver(FormDataSchema),
      defaultValues: {
        name: cart.name || "",
        floorTableId: cart.floorTableId || undefined,
        pickUpInMinutes: cart.pickUpInMinutes || undefined,
      },
    });
  const { errors } = formProps.formState;

  const onCartDetailsChanged = async (data: SimpleOrderFormData) => {
    const response = await updateCart({
      uuid: cart.uuid,
      venueId: cart.venueId,
      name: data.name,
      floorTableId: data.floorTableId || undefined,
      pickUpInMinutes: data.pickUpInMinutes,
    });
    dispatch({
      action: "updateCart",
      payload: response,
    });
  };
  const checkoutCart = async (data: SimpleOrderFormData) => {
    await onCartDetailsChanged(data);

    if (!orderingEnabled) {
      console.error("Ordering is not enabled");
      return;
    }
    const checkoutUrl = await createCheckoutSession(cart.uuid);
    window.location.assign(checkoutUrl);
    await new Promise((r) => setTimeout(r, 3000));
  };

  const onlineOrderingAvailable = useOnlineOrderingAvailableNow();
  const checkoutButton = (
    <Button
      disabled={!onlineOrderingAvailable}
      submissionState={submissionState}
      primary
    >
      {t("cart.payNow")} <Price showDecimals showCurrency value={cartTotal} />
    </Button>
  );
  const initialOrderType =
    orderingEnabled && tableServiceEnabled ? "eatIn" : "takeaway";
  const [orderType, setOrderType] = useState<"takeaway" | "eatIn">(
    initialOrderType,
  );
  const recordChanges = () => {
    trackSubmit(handleSubmit(onCartDetailsChanged))(getValues());
  };
  return (
    <form onSubmit={handleSubmit(trackSubmit(checkoutCart))}>
      <div className="space-y-32 md:space-y-32 md:border-[6px] border-venue-tertiary rounded-venue-brand md:p-16 md:max-w-screen-sm md:mx-auto">
        <div className="flex-1 space-y-32">
          <OnlineOrderingDisabled />
          {orderingEnabled && tableServiceEnabled && (
            <PickUpOptions
              onChange={setOrderType}
              orderType={orderType}
              isEditable
            />
          )}
          <CartItems />
          <CartDetailsForm
            register={register}
            isHaveHere={orderType == "eatIn"}
            onChange={recordChanges}
            errors={errors}
            isEditable
          />
          <div className="">
            <LinkButton secondary to="/">
              {t("cart.addMoreItems")}
            </LinkButton>
          </div>
          <div className="flex  items-center space-x-32">
            <div className="flex-none">
              <div className="text-2xl font-extrabold">
                <Price showCurrency value={cartTotal} />{" "}
              </div>
            </div>
            {orderingEnabled && (
              <div className="hidden md:block flex-1">{checkoutButton}</div>
            )}
          </div>
          <OrderedOrderItems />
        </div>
      </div>
      {orderingEnabled && <PrimaryAction>{checkoutButton}</PrimaryAction>}
    </form>
  );
};
